import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, query } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { InputField } from './SharedComponents';
import { User, Building, UserCheck, Calendar, AlertTriangle, DollarSign, Search, Loader, Hash } from 'lucide-react';

const getLocalISOString = () => {
    const date = new Date();
    const timezoneOffset = date.getTimezoneOffset() * 60000; 
    const localISOTime = new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
    return localISOTime;
};

const RegistrarMulta = () => {
    const fiscalizadores = {
      '45787457': 'AMES FABIAN YESSENIA TANIA',
      '75277219': 'QUISPE CHIHUANCO EDWIN JOEL',
      '15358040': 'SUPA SIERRA PEDRO',
      '43473984': 'MIGUEL LAZARO YEYNOR SÁUL',
      '41991166': 'OJOSE HUAMANI AGUSTINA',
      '40102366': 'VELIZ ALIAGA PELAYO'
    };

    const [formData, setFormData] = useState({
        numero_infraccion: '',
        ruc_dni: '',
        razon_social_nombre: '',
        direccion: '',
        nombre_establecimiento: '',
        propietario_dni: '',
        propietario_nombre: '',
        fiscalizador_dni: '',
        fiscalizador_nombre: '',
        fecha_infraccion: getLocalISOString(),
        tipo_infraccion_id: '',
        observaciones: '',
    });
    const [infracciones, setInfracciones] = useState([]);
    const [monto, setMonto] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isApiLoading, setIsApiLoading] = useState(false);
    const [isPropietarioApiLoading, setIsPropietarioApiLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Estados para la búsqueda de infracciones
    const [infraccionSearch, setInfraccionSearch] = useState('');
    const [filteredInfracciones, setFilteredInfracciones] = useState([]);
    const [showInfraccionesDropdown, setShowInfraccionesDropdown] = useState(false);
    const infraccionDropdownRef = React.useRef(null);


    useEffect(() => {
        const path = `artifacts/${appId}/public/data/tipos_infraccion`;
        const q = query(collection(db, path));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const infraccionesData = [];
            querySnapshot.forEach((doc) => {
                infraccionesData.push({ id: doc.id, ...doc.data() });
            });
            setInfracciones(infraccionesData);
        });
        
        const handleClickOutside = (event) => {
            if (infraccionDropdownRef.current && !infraccionDropdownRef.current.contains(event.target)) {
                setShowInfraccionesDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            unsubscribe();
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleApiSearch = async () => {
        const doc = formData.ruc_dni.trim();
        if (doc.length !== 8 && doc.length !== 11) {
            setMessage({ text: 'Por favor, ingrese un número de RUC (11 dígitos) o DNI (8 dígitos) válido.', type: 'error' });
            return;
        }
        setIsApiLoading(true);
        setMessage({ text: '', type: '' });
        const esRuc = doc.length === 11;
        const proxyApiUrl = `./proxy-api.php?tipo=${esRuc ? 'ruc' : 'dni'}&numero=${doc}`;
        try {
            const response = await fetch(proxyApiUrl);
            const data = await response.json();
            if (!response.ok || data.success === false) {
                 throw new Error(data.message || 'No se encontraron datos.');
            }
            setFormData(prev => ({
                ...prev,
                razon_social_nombre: esRuc 
                    ? (data.data.nombre_o_razon_social || '') 
                    : `${data.data.nombres} ${data.data.apellido_paterno} ${data.data.apellido_materno}`,
                direccion: esRuc ? (data.data.direccion || 'N/A') : 'N/A',
            }));
            setMessage({ text: 'Datos del infractor encontrados.', type: 'success' });
        } catch (error) {
            console.error("Error en API:", error);
            setMessage({ text: error.message, type: 'error' });
        } finally {
            setIsApiLoading(false);
        }
    };

    const handlePropietarioApiSearch = async () => {
        const doc = formData.propietario_dni.trim();
        if (doc.length !== 8) {
            setMessage({ text: 'Por favor, ingrese un DNI de propietario válido (8 dígitos).', type: 'error' });
            return;
        }
        setIsPropietarioApiLoading(true);
        setMessage({ text: '', type: '' });
        const proxyApiUrl = `./proxy-api.php?tipo=dni&numero=${doc}`;
        try {
            const response = await fetch(proxyApiUrl);
            const data = await response.json();
            if (!response.ok || data.success === false) {
                 throw new Error(data.message || 'No se encontraron datos del propietario.');
            }
            setFormData(prev => ({
                ...prev,
                propietario_nombre: `${data.data.nombres} ${data.data.apellido_paterno} ${data.data.apellido_materno}`,
            }));
            setMessage({ text: 'Datos del propietario encontrados.', type: 'success' });
        } catch (error) {
            console.error("Error en API propietario:", error);
            setMessage({ text: error.message, type: 'error' });
        } finally {
            setIsPropietarioApiLoading(false);
        }
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleFiscalizadorDniChange = (e) => {
        const dni = e.target.value;
        const nombreFiscalizador = fiscalizadores[dni] || '';
        setFormData(prev => ({
            ...prev,
            fiscalizador_dni: dni,
            fiscalizador_nombre: nombreFiscalizador
        }));
    };

    const handleInfraccionSearchChange = (e) => {
        const searchTerm = e.target.value;
        setInfraccionSearch(searchTerm);

        if (searchTerm) {
            const filtered = infracciones.filter(inf => 
                inf.codigo.toLowerCase().includes(searchTerm.toLowerCase()) || 
                inf.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredInfracciones(filtered);
            setShowInfraccionesDropdown(true);
        } else {
            setFilteredInfracciones([]);
            setShowInfraccionesDropdown(false);
            setFormData(prev => ({ ...prev, tipo_infraccion_id: '' }));
            setMonto(0);
        }
    };

    const handleInfraccionSelect = (infraccion) => {
        setInfraccionSearch(`${infraccion.codigo} - ${infraccion.descripcion}`);
        setFormData(prev => ({ ...prev, tipo_infraccion_id: infraccion.id }));
        setMonto(infraccion.monto);
        setShowInfraccionesDropdown(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ text: '', type: '' });
        
        try {
            const path = `artifacts/${appId}/public/data/multas`;
            await addDoc(collection(db, path), { ...formData, monto, estado: 'pendiente', fecha_registro: new Date().toISOString() });
            setMessage({ text: 'Multa registrada exitosamente.', type: 'success' });
            setFormData({
                numero_infraccion: '',
                ruc_dni: '', razon_social_nombre: '', direccion: '', nombre_establecimiento: '',
                propietario_dni: '', propietario_nombre: '',
                fiscalizador_dni: '', fiscalizador_nombre: '', 
                fecha_infraccion: getLocalISOString(),
                tipo_infraccion_id: '', observaciones: ''
            });
            setMonto(0);
            setInfraccionSearch('');
        } catch (error) {
            console.error("Error al registrar multa: ", error);
            setMessage({ text: 'Error al registrar la multa.', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
             {message.text && (
                <div className={`p-4 rounded-md ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message.text}
                </div>
            )}
            
            {/* --- NUEVA ESTRUCTURA DE FILAS --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField icon={<Hash />} label="Número de Infracción" name="numero_infraccion" value={formData.numero_infraccion} onChange={handleChange} required />
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">RUC / DNI del Infractor</label>
                    <div className="flex gap-2">
                        <InputField icon={<User />} name="ruc_dni" value={formData.ruc_dni} onChange={handleChange} required containerClassName="flex-grow" />
                        <button type="button" onClick={handleApiSearch} disabled={isApiLoading} className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors flex items-center justify-center">
                            {isApiLoading ? <Loader className="animate-spin w-5 h-5"/> : <Search className="w-5 h-5"/>}
                        </button>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField icon={<Building />} label="Razón Social / Nombre del Infractor" name="razon_social_nombre" value={formData.razon_social_nombre} onChange={handleChange} required />
                <InputField icon={<Building />} label="Dirección del Establecimiento" name="direccion" value={formData.direccion} onChange={handleChange} />
            </div>

            <InputField icon={<Building />} label="Nombre del Establecimiento" name="nombre_establecimiento" value={formData.nombre_establecimiento} onChange={handleChange} required/>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-b py-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">DNI Propietario de Inmueble</label>
                    <div className="flex gap-2">
                        <InputField icon={<User />} name="propietario_dni" value={formData.propietario_dni} onChange={handleChange} containerClassName="flex-grow" />
                        <button type="button" onClick={handlePropietarioApiSearch} disabled={isPropietarioApiLoading} className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors flex items-center justify-center">
                            {isPropietarioApiLoading ? <Loader className="animate-spin w-5 h-5"/> : <Search className="w-5 h-5"/>}
                        </button>
                    </div>
                </div>
                <InputField icon={<User />} label="Nombres Propietario de Inmueble" name="propietario_nombre" value={formData.propietario_nombre} onChange={handleChange} readOnly className="bg-gray-100" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField icon={<UserCheck />} label="DNI del Fiscalizador" name="fiscalizador_dni" value={formData.fiscalizador_dni} onChange={handleFiscalizadorDniChange} required />
                <InputField icon={<UserCheck />} label="Nombre del Fiscalizador" name="fiscalizador_nombre" value={formData.fiscalizador_nombre} onChange={handleChange} readOnly className="bg-gray-100"/>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                <InputField icon={<Calendar />} label="Fecha y Hora de Infracción" name="fecha_infraccion" type="datetime-local" value={formData.fecha_infraccion} onChange={handleChange} />
                <div ref={infraccionDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Infracción</label>
                    <div className="relative">
                        <AlertTriangle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={infraccionSearch}
                            onChange={handleInfraccionSearchChange}
                            onFocus={() => { if (infraccionSearch) setShowInfraccionesDropdown(true) }}
                            placeholder="Buscar por código o descripción..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            autoComplete="off"
                            required
                        />
                         {showInfraccionesDropdown && filteredInfracciones.length > 0 && (
                            <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {filteredInfracciones.map(inf => (
                                    <li 
                                        key={inf.id}
                                        onClick={() => handleInfraccionSelect(inf)}
                                        className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                                    >
                                        <strong className="text-blue-600">{inf.codigo}</strong> - {inf.descripcion}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="relative">
                 <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                 <input type="text" readOnly value={`Monto: S/ ${monto.toFixed(2)}`} className="w-full pl-10 bg-gray-100 py-2 border border-gray-300 rounded-md" />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea name="observaciones" value={formData.observaciones} onChange={handleChange} rows="3" className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"></textarea>
            </div>
            
            <div className="flex justify-end">
                <button type="submit" disabled={isLoading} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors">
                    {isLoading ? 'Registrando...' : 'Registrar Multa'}
                </button>
            </div>
        </form>
    );
};

export default RegistrarMulta;